import React, { useState, useContext } from 'react'
import ReactMarkdown from 'react-markdown'
import { Link } from 'react-router-dom'
import { Image, StarRating } from '@deal/components'
import useInterval from '#src/hooks/useInterval'
import { Experiment } from '@deal/experiment-js'
import { DepartmentContext } from '#src/context/department'
import ContentfulQuery from '#src/containers/Contentful'
import { contentfulTemplateMap } from '#src/services/contentful'
import { CategoryType } from '#src/generated/graphql'
import useBreakpoint from '#src/hooks/useBreakpoint'
import PriceMatchIcon from './price-match.svg'
import FreeShippingIcon from './free-shipping.svg'
import PageContent from '../PageContent'
import styles from './styles.css'
import classnames from 'classnames'

interface BannerType {
  iconOne?: { imageUrl: string }
  textOne?: string
  iconTwo?: { imageUrl: string }
  textTwo?: string
}
interface ContentfulResponse {
  content: { banners: BannerType[] }
  loading: boolean
}

const ROTATION_SPEED = 3000

interface BannerProps {
  banner: BannerType
  sidebar?: boolean
  isMobile: boolean
  departmentSlug: string
  active: boolean
}

const Banner: React.SFC<BannerProps> = ({
  banner: { iconOne, textOne, iconTwo, textTwo },
  sidebar,
  isMobile,
  departmentSlug,
  active
}) => (
  <div className={classnames(styles.bannerBackground, { [styles.hide]: !active })}>
    <PageContent sidebar={sidebar} className={styles.guaranteeBanner}>
      <span className={styles.guarantee}>
        {iconOne && (
          <Image width={15} height={15} src={iconOne.imageUrl} className={styles.image} />
        )}
        {textOne && <ReactMarkdown source={textOne} className={styles.markdown} />}
      </span>
      {isMobile ? (
        <BannerReviews departmentSlug={departmentSlug} />
      ) : iconTwo || textTwo ? (
        <>
          <div className={styles.break} />
          <span className={styles.guarantee}>
            {iconTwo && (
              <Image width={15} height={15} src={iconTwo.imageUrl} className={styles.image} />
            )}
            {textTwo && <ReactMarkdown className={styles.markdown} source={textTwo} />}
          </span>
        </>
      ) : null}
    </PageContent>
  </div>
)

interface DefaultProductBannerProps {
  sidebar?: boolean
  isMobile: boolean
  departmentSlug: string
}

const DefaultProductBanner: React.SFC<DefaultProductBannerProps> = ({
  sidebar,
  isMobile,
  departmentSlug
}) => (
  <div className={styles.bannerBackground}>
    <PageContent sidebar={sidebar} className={styles.guaranteeBanner}>
      <span className={styles.guarantee}>
        <FreeShippingIcon />
        <div className={styles.defaultLabel}>
          <span className={styles.bold}>Free shipping</span> on orders over $50
        </div>
      </span>
      {isMobile ? (
        <BannerReviews departmentSlug={departmentSlug} />
      ) : (
        <>
          <div className={styles.break} />
          <span className={styles.guarantee}>
            <PriceMatchIcon />
            <div className={styles.defaultLabel}>
              <span className={styles.bold}>Price match</span> guarantee
            </div>
          </span>
        </>
      )}
    </PageContent>
  </div>
)

interface BannerReviewsProps {
  departmentSlug: string
}

const BannerReviews: React.SFC<BannerReviewsProps> = ({ departmentSlug }) => (
  <>
    <div className={styles.reviewsBreak} />
    <Link to={`/reviews/${departmentSlug}`} className={styles.reviewsContainer}>
      <StarRating rating={5} starRatedColor="#f2962d" />
      <div className={styles.reviewsLink}>See our reviews</div>
    </Link>
  </>
)

interface RotateProps {
  banners: BannerType[]
  sidebar?: boolean
  isMobile: boolean
  departmentSlug: string
}

const Rotate: React.SFC<RotateProps> = ({ banners, sidebar, isMobile, departmentSlug }) => {
  const [bannerNumber, setBannerNumber] = useState(0)
  useInterval(() => {
    const nextBannerNumber = bannerNumber + 1 === banners.length ? 0 : bannerNumber + 1
    setBannerNumber(nextBannerNumber)
  }, ROTATION_SPEED)

  // Render all banners and use CSS to hide to avoid re-fetching SVGs on every banner rotation
  return (
    <>
      {banners.map((banner, idx) => (
        <Banner
          key={idx}
          active={idx === bannerNumber}
          isMobile={isMobile}
          departmentSlug={departmentSlug}
          banner={banner}
          sidebar={sidebar}
        />
      ))}
    </>
  )
}

const PageBanner: React.SFC<{ sidebar?: boolean }> = ({ sidebar }) => {
  const isMobile = !useBreakpoint('sm')
  const { department } = useContext(DepartmentContext)

  if (!department) {
    /**
     * If there is no department we don't show a banner
     */
    return null
  } else {
    const { slug } = department
    const splitExperiemnt = `banner-${slug}`

    return (
      <Experiment id={splitExperiemnt} default="off">
        {({ result }) => {
          if (result === 'off') {
            return null
          } else if (!result || result === 'control') {
            // Experiment isn't set up so return default if product department
            return department.topLevelCategory &&
              department.topLevelCategory.type === CategoryType.PRODUCT ? (
              <DefaultProductBanner sidebar={sidebar} isMobile={isMobile} departmentSlug={slug} />
            ) : null
          }

          const spaceId = contentfulTemplateMap['bannerVariations']
          return (
            <ContentfulQuery space={spaceId} template="bannerVariations" variation={result}>
              {({ content }: ContentfulResponse) => {
                if (content && content.banners) {
                  return (
                    <Rotate
                      banners={content.banners}
                      sidebar={sidebar}
                      isMobile={isMobile}
                      departmentSlug={slug}
                    />
                  )
                } else if (
                  department.topLevelCategory &&
                  department.topLevelCategory.type === CategoryType.PRODUCT
                ) {
                  return (
                    <DefaultProductBanner
                      sidebar={sidebar}
                      isMobile={isMobile}
                      departmentSlug={slug}
                    />
                  )
                } else {
                  return null
                }
              }}
            </ContentfulQuery>
          )
        }}
      </Experiment>
    )
  }
}

export default PageBanner
